import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Tooltip } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

export interface RolesFormValues {
  isAdministratorRole: boolean;
  isInstallerRole: boolean;
}

interface Props<T extends FieldValues & RolesFormValues> {
  control: Control<T>;
  disableAdministratorRole?: boolean;
  disableInstallerRole?: boolean;
  administratorDisabledTooltip?: string;
  installerDisabledTooltip?: string;
  selectAtLeastOneRoleValidationMessage?: string;
  selectAtLeastOneRoleError?: string;
  onRoleChange?: () => void;
}

const RolesSection = <T extends FieldValues & RolesFormValues>({
  control,
  disableAdministratorRole,
  disableInstallerRole,
  administratorDisabledTooltip,
  installerDisabledTooltip,
  selectAtLeastOneRoleValidationMessage,
  selectAtLeastOneRoleError,
  onRoleChange
}: Props<T>) => {
  const { translation } = useTranslation();
  return (
    <FormControl component="fieldset">
      <FormLabel component="legend" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }} color="secondary">
        {translation.roles}
        <Tooltip title={translation.roles_tooltip}>
          <InfoOutlined fontSize="small" />
        </Tooltip>
      </FormLabel>
      <FormGroup>
        <Controller
          name={'isAdministratorRole' as Path<T>}
          control={control}
          rules={{
            validate: (_, formValues) =>
              !selectAtLeastOneRoleValidationMessage ||
              formValues.isAdministratorRole ||
              formValues.isInstallerRole ||
              selectAtLeastOneRoleValidationMessage
          }}
          render={({ field }) => (
            <Tooltip
              title={disableAdministratorRole && administratorDisabledTooltip ? administratorDisabledTooltip : ''}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    id="isAdministratorRole-checkbox"
                    checked={!!field.value}
                    onChange={(event) => {
                      field.onChange(event);
                      onRoleChange?.();
                    }}
                    color="primary"
                    disabled={disableAdministratorRole}
                  />
                }
                label={translation.administrator}
              />
            </Tooltip>
          )}
        />
        <Controller
          name={'isInstallerRole' as Path<T>}
          control={control}
          render={({ field }) => (
            <Tooltip title={disableInstallerRole && installerDisabledTooltip ? installerDisabledTooltip : ''}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="isInstallerRole-checkbox"
                    checked={!!field.value}
                    onChange={(event) => {
                      field.onChange(event);
                      onRoleChange?.();
                    }}
                    color="primary"
                    disabled={disableInstallerRole}
                  />
                }
                label={translation.installer}
              />
            </Tooltip>
          )}
        />
      </FormGroup>
      {selectAtLeastOneRoleError && <FormHelperText error>{selectAtLeastOneRoleError}</FormHelperText>}
    </FormControl>
  );
};

export default RolesSection;
